import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Table, Tag, Progress } from 'antd';
import { floor, toLower } from '../../../../util/lodashlike';
import { normalizeAlertScore } from '../../../../util/normalization';
import useAlertLevelConfigs from '../../../common/hooks/useAlertLevelConfig';
import {
  MultiHeader,
  SingleHeader,
  SingleDataColumn,
  MultiDataColumn
} from '../Components';
import { ResultTableWrapper } from './styled';
import { convertISOToDateFormat } from '../../../../util/timezone';
import { TextComponent } from '../../components/TableComponents';
import {
  TM_FRAUD_PREVENTION_SOURCE_MAPPING,
  TM_FRAUD_SOURCE_IDS
} from '../../../../util/GlobalConstant';

// 24 = RealtimeFraudIadRunInstance, 25 = RealtimeFraudIadProduction,
// 29 = RealtimeFraudIadProductionBatch — see be/.../enums/AlertSource.scala.
// Run-instance alerts persist with source 24, so it must be included or the
// Score/Level columns render em-dashes on the run-instance listing.
const FRAML_P_SOURCE_IDS = new Set(['24', '25', '29']);

// Canonical-code fallback colours, used only until the custom Level Name Setting
// (alert_level_details) loads. Mid band is "Good" (FIN-12077), not "Medium".
const severityColorMap = { High: '#ff4d4f', Good: '#faad14', Low: '#52c41a' };
const levelTagColorMap = { High: 'red', Good: 'orange', Low: 'green' };

const PreventionResultTable = ({
  isLoading,
  results,
  paginationProps,
  onPaginationChange,
  totalResults,
  onAlertIdClick,
  hasAccessToAlertDetails,
  typlogyActions,
  fetchTypologiesActions,
  fetchAMLSAlertLevel,
  alertLevels,
  // FIN-13287 — server-side Level filter. `onLevelFilterChange` reports the
  // column's selected values up to Result.js, which owns `sortFilterObj` and
  // the fetch; `alertLevelFilterValue` is that same state read back, so the
  // funnel icon always reflects what is actually applied (never a separate,
  // driftable local state).
  onLevelFilterChange,
  alertLevelFilterValue = []
}) => {
  // FIN-12134: custom alert-level names/colours (Level Name Setting) for the
  // Score + Level columns, keyed by the canonical code emitted by the engine.
  const { getLevelLabel, getLevelColor, formatAlertLevelOptions } =
    useAlertLevelConfigs(alertLevels);

  useEffect(() => {
    if (!typlogyActions?.length) {
      fetchTypologiesActions();
    }
    // react-redux v5 has no hooks; fetch via the connected action prop (not useDispatch).
    if (!alertLevels?.length && typeof fetchAMLSAlertLevel === 'function') {
      fetchAMLSAlertLevel();
    }
  }, []);

  // Custom level names for the Level column filter; fall back to canonical codes
  // until the config loads so the filter is never empty.
  const levelFilterOptions = formatAlertLevelOptions();
  const levelFilters = (
    levelFilterOptions.length
      ? levelFilterOptions
      : [
          { label: 'Low', value: 'Low' },
          { label: 'Good', value: 'Good' },
          { label: 'High', value: 'High' }
        ]
  ).map(({ label, value }) => ({ text: label, value }));

  // FIN-12133: colour-code the Action by its configured severity's level colour
  // (action_severity -> alert_level_details colour), consistent with the Score
  // Explanation. typlogyActions[].alertLevelColor was never populated by the API.
  const preventionAlertColors = (key) =>
    getLevelColor(
      typlogyActions?.find(
        ({ displayName }) => toLower(displayName) === toLower(key)
      )?.actionSeverity
    );

  const columns = [
    {
      title: (
        <MultiHeader header="Alert ID" subHeaders={['Alert Generation Date']} />
      ),
      width: '15%',
      ellipsis: true,
      render: (_, record) => {
        const {
          alertId = '-',
          alertDate = '-',
          reviewStatus = 'Not Started',
          alertSourceId
        } = record;

        if (hasAccessToAlertDetails) {
          return (
            /* `combined` — ONE hover card for the whole cell, as the TM-IAD
               table got in FIN-14012. The id and the date are read together and
               the id is what the ellipsis cuts, so a card repeating both lines
               is what makes the full value reachable. BOTH branches of this
               column carry it: the link and the plain renders are the same cell
               to a user, and only one of them having a card would look like the
               card is broken on half the rows. */
            <MultiDataColumn.Link
              combined
              data={[
                {
                  text: alertId,
                  to: `/app/trm/run-instance/${alertId}`,
                  ...(toLower(reviewStatus) === 'not started'
                    ? {
                        onClick: () => {
                          onAlertIdClick(alertId, alertSourceId);
                        }
                      }
                    : {})
                },
                {
                  text: convertISOToDateFormat(alertDate) || '-'
                }
              ]}
            />
          );
        } else {
          return (
            <MultiDataColumn
              combined
              data={[
                { text: alertId },
                {
                  text: convertISOToDateFormat(alertDate) || '-'
                }
              ]}
            />
          );
        }
      }
    },
    // {
    //   title: <MultiHeader header="Party ID" subHeaders={['Party Name']} />,
    //   width: '15%',
    //   ellipsis: true,
    //   render: (_, record) => {
    //     const { primaryPartyKey = '-', primaryPartyName = '-' } = record;
    //     return (
    //       <MultiDataColumn
    //         data={[{ text: primaryPartyKey }, { text: primaryPartyName }]}
    //         pop
    //       />
    //     );
    //   }
    // },
    // {
    //   title: <MultiHeader header="Account NO" subHeaders={['Account Type']} />,
    //   width: '10%',
    //   ellipsis: true,
    //   render: (_, record) => {
    //     const { accountId = '-', accountType = '-' } = record;
    //     return (
    //       <MultiDataColumn
    //         pop
    //         data={[{ text: accountId }, { text: accountType }]}
    //       />
    //     );
    //   }
    // },
    {
      title: (
        <MultiHeader
          header="Run Instance Name"
          subHeaders={['Run Instance ID', 'Run Instance Execution ID']}
        />
      ),
      width: '13%',
      ellipsis: true,
      render: (_, record) => {
        const {
          runInstanceName = '-',
          runInstanceId = '-',
          runInstanceExecutionId = '-'
        } = record;
        return (
          /* THREE lines here, not two — name, id and execution id. `revealable`
             drops the ones that are '-', so a run instance carrying only an
             execution id shows a one-line card rather than two placeholder
             dashes above the value. */
          <MultiDataColumn
            combined
            data={[
              { text: runInstanceName },
              { text: runInstanceId },
              { text: runInstanceExecutionId }
            ]}
          />
        );
      }
    },
    // {
    //   title: (
    //     <MultiHeader header="Typology Name" subHeaders={['Typology ID']} />
    //   ),
    //   width: '10%',
    //   ellipsis: true,
    //   render: (_, record) => {
    //     const { triggeredTypologies = [] } = record;
    //     const typologyNames = map(triggeredTypologies, 'name').join(', ');
    //     const typologyIds = map(triggeredTypologies, 'id').join(', ');
    //     return (
    //       <MultiDataColumn
    //         pop
    //         data={[
    //           { text: isEmpty(typologyNames) ? '-' : typologyNames },
    //           { text: isEmpty(typologyIds) ? '-' : typologyIds }
    //         ]}
    //       />
    //     );
    //   }
    // },
    {
      title: <SingleHeader header="FinCrime Type" />,
      width: '9%',
      render: (_, record) => {
        const { alertSourceId = '' } = record;
        return (
          <TextComponent
            style={{ marginLeft: 0 }}
            id={
              alertSourceId === TM_FRAUD_SOURCE_IDS.TM
                ? TM_FRAUD_PREVENTION_SOURCE_MAPPING.TM_AML
                : alertSourceId === TM_FRAUD_SOURCE_IDS.FRAUD_PREVENTION_ID
                ? TM_FRAUD_PREVENTION_SOURCE_MAPPING.FRAUD_PREVENTION
                : TM_FRAUD_PREVENTION_SOURCE_MAPPING.FRAUD_DETECTION
            }
          />
        );
      }
    },
    {
      title: <SingleHeader header="Actions taken for the variants breached" />,
      width: '10%',
      render: (_, record) => {
        const { triggeredRulesActionSummary = '-' } = record;
        if (triggeredRulesActionSummary == '-') {
          return '-';
        }
        if (triggeredRulesActionSummary && typlogyActions) {
          const triggerConverted = JSON.parse(triggeredRulesActionSummary);
          const keys = Object.keys(triggerConverted);
          const values = Object.values(triggerConverted);
          return (
            <>
              {keys.map((key, index) => (
                <div
                  key={key}
                  style={{
                    color: preventionAlertColors(key)
                  }}>
                  {key}: {values[index]}
                </div>
              ))}
            </>
          );
        }
      }
    },
    {
      title: <SingleHeader header="Review Status" />,
      width: '9%',
      render: (_, record) => {
        const { reviewStatus = 'Not Started' } = record;
        return <SingleDataColumn.Status type={reviewStatus} />;
      }
    },
    {
      title: <SingleHeader header="Review Feedback" />,
      width: '10%',
      render: (_, record) => {
        const { alertQuality = '-' } = record;
        return (
          <SingleDataColumn.Feedback
            type={alertQuality}
            alertLevels={alertLevels}
          />
        );
      }
    },
    {
      title: <SingleHeader header="Score" />,
      dataIndex: 'alertScore',
      key: 'alertScore',
      width: '10%',
      sorter: (a, b) => (a.alertScore || 0) - (b.alertScore || 0),
      render: (score, record) => {
        if (
          score == null ||
          !FRAML_P_SOURCE_IDS.has(String(record.alertSourceId))
        ) {
          return '—';
        }
        const level = record.alertLevel;
        const color =
          getLevelColor(level) ||
          severityColorMap[level] ||
          severityColorMap.Low;
        // FIN-13288 — same transform as the Smart Assessment top panel
        // (SmartAssessmentTopPanel renders normalizeAlertScore(floor(alertScore, 2))),
        // so one alert cannot read differently in the two views. This showed 1dp while
        // that one floors to 2dp, so alertScore 62.51 rendered as 62.5 here. Reusing
        // those helpers rather than toFixed(2) also keeps the rounding MODE aligned --
        // toFixed rounds where floor truncates, so they would still disagree on 62.519.
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Progress
              percent={score}
              size="small"
              strokeColor={color}
              showInfo={false}
              style={{ width: 60, display: 'inline-block' }}
            />
            {normalizeAlertScore(floor(score, 2))}
          </span>
        );
      }
    },
    {
      title: <SingleHeader header="Level" />,
      dataIndex: 'alertLevel',
      key: 'alertLevel',
      width: '8%',
      filters: levelFilters,
      // FIN-13287 — the grid is server-paginated (one page of results at a
      // time), so antd's client-side `onFilter` could only ever search the
      // rows currently loaded and returned "No data" for a level that exists
      // elsewhere on the server. The selection is now routed server-side via
      // `onLevelFilterChange` (Table onChange below) and `alertLevelFilterValue`
      // (the controlled `filteredValue`) — no client-side `onFilter` here.
      //
      // [adversarial S3(a)] Single-select only. The BE's `alertLevel` branch
      // takes one value; antd defaults a column filter to multi-select, and
      // a comma-joined multi-value selection would silently match zero rows.
      filterMultiple: false,
      filteredValue: alertLevelFilterValue,
      render: (level, record) => {
        if (!level || !FRAML_P_SOURCE_IDS.has(String(record.alertSourceId))) {
          return '—';
        }
        return (
          <Tag
            color={
              getLevelColor(level) || levelTagColorMap[level] || 'default'
            }>
            {getLevelLabel(level)}
          </Tag>
        );
      }
    }
  ];

  return (
    <ResultTableWrapper>
      <Table
        loading={isLoading}
        columns={columns}
        dataSource={results}
        rowKey="alertId"
        pagination={{
          total: totalResults,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: onPaginationChange,
          showTotal: (total) => `${total} Records`,
          ...paginationProps
        }}
        // FIN-13287 — antd reports every column filter change here regardless
        // of which column changed, so only the Level column's own selection is
        // forwarded up.
        //
        // FIN-13573 — and antd fires this for PAGE and PAGE-SIZE changes too,
        // not only filter changes. Forwarding unconditionally meant every page
        // click also ran `onLevelFilterChange`, whose first act is
        // `setPagination(DEFAULT_PAGINATION_PROPS)` (Result.js) — so the click
        // set page 2 and this reset it straight back to page 1 in the same
        // batch, and no page past the first was ever reachable. Bail out unless
        // the selection actually differs from what is already applied.
        onChange={(_pagination, filters) => {
          const next = filters.alertLevel || [];
          const applied = alertLevelFilterValue || [];
          const unchanged =
            next.length === applied.length &&
            next.every((value, i) => value === applied[i]);
          if (unchanged) return;
          onLevelFilterChange?.(next);
        }}
      />
    </ResultTableWrapper>
  );
};

PreventionResultTable.propTypes = {
  results: PropTypes.array,
  isLoading: PropTypes.bool,
  totalResults: PropTypes.number,
  paginationProps: PropTypes.object,
  onPaginationChange: PropTypes.func,
  onAlertIdClick: PropTypes.func,
  typlogyActions: PropTypes.array,
  fetchTypologiesActions: PropTypes.func,
  fetchAMLSAlertLevel: PropTypes.func,
  alertLevels: PropTypes.array,
  onLevelFilterChange: PropTypes.func,
  alertLevelFilterValue: PropTypes.array
};

export default PreventionResultTable;
